import { ChartData } from "chart.js";

import { Customer } from "@typings/Customer";
import { Licence } from "@typings/Licence";
import { Partner } from "@typings/Partner";
import { Tag } from "@typings/Tag";

const BACKGROUND_COLORS = [
  "#20B2AA",
  "#DC143C",
  "#8A2BE2",
  "#0BC15D",
  "#FFD700",
  "#878787",
];

const formatDate = (dateIso: string) => {
  const date = new Date(dateIso);
  const day = date.getDate();
  return `${day < 10 ? `0${day}` : day} ${date.toLocaleString("default", {
    month: "short",
  })}`;
};

const buildMonthLabels = (month: Date | null) => {
  if (month) {
    const monthIso = month.toISOString();
    // Get the number of days in the month
    const year = parseInt(monthIso.split("-")[0]);
    const monthIndex = parseInt(monthIso.split("-")[1]);
    const daysInMonth = new Date(year, monthIndex, 0).getDate();

    // Create an array for the days in the month
    return Array.from(
      { length: daysInMonth },
      (_, i) =>
        new Date(year, monthIndex - 1, i + 2).toISOString().split("T")[0],
    );
  }

  // Create an array for the last 30 calendar days
  return Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().split("T")[0];
  }).reverse();
};

class DashboardUtils {
  calculateLicencesChart(
    licences: Licence[],
    tags: Tag[],
  ): ChartData<"doughnut", number[], string> {
    const labels: string[] = [...new Set(licences.map(l => l.tag || "No Tag"))];
    const tagCounts: number[] = labels.map(
      tag => licences.filter(l => l.tag === tag).length,
    );
    const tagColors = labels.map(
      tag => tags.find(t => t.name === tag)?.color || "rgb(255, 205, 86)",
    );

    const dataArr = labels.map((label, index) => ({
      label,
      count: tagCounts[index],
      color: tagColors[index],
    }));

    dataArr.sort((a, b) => b.count - a.count);

    const sortedLabels = dataArr.map(item => item.label);
    const sortedCounts = dataArr.map(item => item.count);
    const sortedColors = dataArr.map(item => item.color);

    const data: ChartData<"doughnut", number[], string> = {
      labels: sortedLabels,
      datasets: [
        {
          label: "Licences",
          data: sortedCounts,
          backgroundColor: sortedColors,
        },
      ],
    };

    return data;
  }

  calculateCustomersChart(
    licences: Licence[],
    customers: Customer[],
  ): ChartData<"doughnut", number[], string> {
    const customerMap = new Map(customers.map(c => [c.id, c.name]));
    const licenceCountMap = new Map<string, number>();

    for (const licence of licences) {
      const count = licenceCountMap.get(licence.customer_id!) || 0;
      licenceCountMap.set(licence.customer_id!, count + 1);
    }

    let results: { name: string; licenceCount: number }[] = Array.from(
      licenceCountMap.entries(),
    ).map(([id, count]) => ({
      name: customerMap.get(id) || "Unknown",
      licenceCount: count,
    }));

    results.sort((a, b) => b.licenceCount - a.licenceCount);

    if (results.length > 5) {
      const otherLicenceCount = results
        .slice(5)
        .reduce((sum, r) => sum + r.licenceCount, 0);
      results = results.slice(0, 5);
      results.push({ name: "Other", licenceCount: otherLicenceCount });
    }

    const labels = results.map(r => r.name);
    const counts = results.map(r => r.licenceCount);
    return {
      labels,
      datasets: [
        {
          label: "Licences",
          data: counts,
          backgroundColor: BACKGROUND_COLORS,
        },
      ],
    };
  }

  calculateReplMigrCreatedChart(
    licences: Licence[],
    month: Date | null,
  ): ChartData<"bar", number[], string> {
    type GroupedData = {
      [key: string]: {
        replicas: number;
        migrations: number;
      };
    };

    // Aggregate by date
    const groupedData = licences.reduce((acc: GroupedData, licence) => {
      const date = new Date(licence.created_at).toISOString().split("T")[0];
      if (!acc[date]) {
        acc[date] = { replicas: 0, migrations: 0 };
      }
      acc[date].replicas += licence.replicas;
      acc[date].migrations += licence.migrations;
      return acc;
    }, {});

    const labels = buildMonthLabels(month);

    const replicasData = labels.map(date => groupedData[date]?.replicas || 0);
    const migrationsData = labels.map(
      date => groupedData[date]?.migrations || 0,
    );

    return {
      labels: labels.map(formatDate),
      datasets: [
        {
          label: "Replicas",
          data: replicasData,
          backgroundColor: "#F91661",
        },
        {
          label: "Migrations",
          data: migrationsData,
          backgroundColor: "#0044CA",
        },
      ],
    };
  }

  calculateLicenceCreatedChart(
    licences: Licence[],
    month: Date | null,
  ): ChartData<"line", number[], string> {
    type GroupedData = {
      [key: string]: number;
    };

    // Aggregate by date
    const groupedData = licences.reduce((acc: GroupedData, licence) => {
      const date = new Date(licence.created_at).toISOString().split("T")[0];
      if (!acc[date]) {
        acc[date] = 0;
      }
      acc[date] += 1;
      return acc;
    }, {});

    const labels = buildMonthLabels(month);
    const licencesCount = labels.map(date => groupedData[date] || 0);

    return {
      labels: labels.map(formatDate),
      datasets: [
        {
          label: "Licences",
          data: licencesCount,
          backgroundColor: "#280E4C",
          borderColor: "#280E4C",
        },
      ],
    };
  }

  calculatePartnerLicencesCreatedChart(
    licences: Licence[],
    customers: Customer[],
    partners: Partner[],
    month: Date | null,
  ): ChartData<"line", number[], string> {
    const adminPartnerId = partners.find(
      partner => partner.name === "Cloudbase Solutions",
    )?.id;

    const customerIds = customers
      .filter(customer => customer.partner_id === adminPartnerId)
      .map(customer => customer.id);

    const filteredLicences = licences.filter(
      licence => !customerIds.includes(licence.customer_id!),
    );

    type GroupedData = {
      [key: string]: number;
    };

    const groupedData = filteredLicences.reduce((acc: GroupedData, licence) => {
      const date = new Date(licence.created_at).toISOString().split("T")[0];
      if (!acc[date]) {
        acc[date] = 0;
      }
      acc[date] += 1;
      return acc;
    }, {});

    const labels = buildMonthLabels(month);
    const licencesCount = labels.map(date => groupedData[date] || 0);

    return {
      labels: labels.map(formatDate),
      datasets: [
        {
          label: "Licences",
          data: licencesCount,
          backgroundColor: "#280E4C",
          borderColor: "#280E4C",
        },
      ],
    };
  }

  calculatePartnersChart(
    licences: Licence[],
    customers: Customer[],
    partners: Partner[],
  ): ChartData<"doughnut", number[], string> {
    const partnerMap = new Map(partners.map(p => [p.id, p.name]));
    const customerToPartnerMap = new Map(
      customers.map(c => [c.id, c.partner_id]),
    );
    const partnerLicenceCountMap = new Map<string, number>();

    for (const licence of licences) {
      const partnerId = customerToPartnerMap.get(licence.customer_id!);
      const count = partnerLicenceCountMap.get(partnerId!) || 0;
      partnerLicenceCountMap.set(partnerId!, count + 1);
    }

    let results: { name: string; licenceCount: number }[] = Array.from(
      partnerLicenceCountMap.entries(),
    ).map(([id, count]) => ({
      name: partnerMap.get(id) || "Unknown",
      licenceCount: count,
    }));

    results.sort((a, b) => b.licenceCount - a.licenceCount);

    if (results.length > 5) {
      const otherLicenceCount = results
        .slice(5)
        .reduce((sum, r) => sum + r.licenceCount, 0);
      results = results.slice(0, 5);
      results.push({ name: "Other", licenceCount: otherLicenceCount });
    }

    const labels = results.map(r => r.name);
    const counts = results.map(r => r.licenceCount);
    return {
      labels,
      datasets: [
        {
          label: "Licences",
          data: counts,
          backgroundColor: BACKGROUND_COLORS,
        },
      ],
    };
  }
}

export default new DashboardUtils();
